import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { LCLShipmentModel } from '@data-layer/models/lcl';
import { CompanyModel } from '@data-layer/models/companies';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BillItemModel, BillItemPendingModel, BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { PaginatedResultModel } from '@data-layer/models/common';

@Component({
  selector: 'app-lcl-shipment-user-charges',
  templateUrl: './lcl-shipment-user-charges.component.html',
  styleUrls: ['./lcl-shipment-user-charges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclShipmentUserChargesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public shipment: LCLShipmentModel;
  @Input() public company: CompanyModel;
  @Input() public payedBills: PaginatedResultModel<BillModel>;
  @Input() public pendingBills: PaymentBillItemPendingModel;

  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  public alreadyPayedBills: PaymentBillItemPendingModel;
  public additionalCharges: PaymentBillItemPendingModel;
  public nonPayedBills: PaymentBillItemPendingModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor() {}

  public ngOnInit(): void {
    this.calculateBills();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes?.payedBills?.currentValue || !!changes?.pendingBills?.currentValue) {
      this.calculateBills();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get totalCharges(): number {
    return (
      (this.additionalCharges?.items || []).reduce(
        (acc: number, current: BillItemPendingModel) => acc + current.price * current.quantity,
        0,
      ) +
      (this.alreadyPayedBills?.items || []).reduce(
        (acc: number, current: BillItemPendingModel) => acc + current.price * current.quantity,
        0,
      ) +
      (this.nonPayedBills?.items || []).reduce((acc: number, current: BillItemPendingModel) => acc + current.price * current.quantity, 0)
    );
  }

  private calculateBills(): void {
    // 3 cases: nothing payed yet; payed; payed + added charges
    this.additionalCharges = null;
    this.nonPayedBills = null;
    this.alreadyPayedBills = null;
    const payedBills: BillItemModel[] = (this.payedBills?.items || []).reduce(
      (acc: BillItemModel[], curr: BillModel) => [...acc, ...curr?.items],
      [],
    );

    if (this.pendingBills?.items?.length && payedBills.length === 0) {
      this.additionalCharges = { items: [], timestamp: null };
      this.nonPayedBills = this.pendingBills;
    } else if (!this.pendingBills?.items?.length && payedBills.length > 0) {
      this.additionalCharges = { items: [], timestamp: null };
      this.alreadyPayedBills = { items: payedBills as unknown as BillItemPendingModel[], timestamp: null };
    } else if (payedBills.length > 0 && this.pendingBills?.items?.length) {
      this.additionalCharges = this.pendingBills;
      this.alreadyPayedBills = { items: payedBills as unknown as BillItemPendingModel[], timestamp: null };
    }
  }
}
